<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useMessagingStore } from '@/stores/messaging'
import { useChatStore } from '@/stores/chat'
import { useBoundaryStore } from '@/stores/boundary'
import { useAccessStore } from '@/stores/access'
import { MessagesSquare, Folder } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { formatTimeFromNow } from '@/utils/datetime'
import { buildStaticMapUrl } from '@/utils/mapbox/staticMap'
import ChatAvatar from '@/pages/Project/Chat/ChatAvatar.vue'

const projectStore = useProjectStore()

const orgStore = useOrgStore()

const userStore = useUserStore()

const messagingStore = useMessagingStore()

const chatStore = useChatStore()

const boundaryStore = useBoundaryStore()

const accessStore = useAccessStore()
const router = useRouter()
const route = useRoute()

const orgId = computed(() => orgStore.selectedOrg)
const me = computed(() => userStore.member)
const conversations = computed(() => messagingStore.conversations)
const projects = computed(() => projectStore.flatedProjects || [])

// The search input lives in MessagingPanel, so the query comes from the store.
const query = computed(() => messagingStore.searchQuery)
const isSearching = computed(() => query.value.trim().length > 0)
const newChatOpen = computed(() => messagingStore.newChatOpen)
// The picker reuses the search sections; with no query it lists everything you
// could start a chat with.
const isPicking = computed(() => newChatOpen.value && !isSearching.value)
const showSections = computed(() => isSearching.value || newChatOpen.value)
const searchPeople = ref([])

// Members come from the server (debounced); agents and projects are already
// in the store, so they filter locally.
let searchTimer = null
watch(query, (value) => {
  const q = value.trim()
  clearTimeout(searchTimer)
  if (q.length < 2) {
    searchPeople.value = []
    return
  }
  searchTimer = setTimeout(async () => {
    try {
      const results = await orgStore.searchMembers({ orgId: orgId.value, query: q })
      searchPeople.value = (results || []).filter((m) => m.member_id !== me.value?.member_id)
    } catch (e) {
      searchPeople.value = []
    }
  }, 250)
})

// People you have no conversation with yet — the picker's default roster.
const peopleToStart = computed(() => {
  const seen = new Set(conversations.value.map((c) => c.peer?.member_id))
  seen.add(me.value?.member_id)
  const roster = []
  for (const member of orgStore.currentMembers || []) {
    if (seen.has(member.member_id)) continue
    seen.add(member.member_id)
    roster.push(member)
  }
  return roster
})

// In the picker the roster is already in the store, so it filters instantly and
// the (slower, 2-char) server results are merged in behind it.
const people = computed(() => {
  if (!newChatOpen.value) return searchPeople.value
  const q = query.value.trim().toLowerCase()
  if (!q) return peopleToStart.value
  const local = peopleToStart.value.filter(
    (m) =>
      (m.display_name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
  )
  const seen = new Set(local.map((m) => m.member_id))
  return [...local, ...searchPeople.value.filter((m) => !seen.has(m.member_id))]
})

// Agent DMs run on the LLM stack, so the agent section is gated on ai_enabled.
const aiEnabled = computed(() => !!orgStore.getSelectedOrg?.features?.ai_enabled)

const agentResults = computed(() => {
  if (!aiEnabled.value) return []
  const agents = messagingStore.agents
  if (isPicking.value) return agents
  const q = query.value.trim().toLowerCase()
  if (!q) return []
  return agents.filter((a) => (a.name || '').toLowerCase().includes(q))
})

const projectResults = computed(() => {
  if (isPicking.value) return projects.value
  const q = query.value.trim().toLowerCase()
  if (!q) return []
  return projects.value
    .filter((p) => (p.project_name || '').toLowerCase().includes(q))
    .slice(0, 8)
})

// Search inside conversations too — across whatever messages are loaded, plus
// each conversation's last message from the list payload. The picker skips
// this section — it starts chats rather than jumping into history.
const projectById = computed(() =>
  Object.fromEntries(projects.value.map((p) => [p.project_id, p]))
)

const messageResults = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q || newChatOpen.value) return []
  const hits = []

  for (const conversation of chatStore.conversations) {
    const loaded = chatStore.flatMessages(conversation.uuid)
    const pool = loaded.length
      ? loaded
      : conversation.lastMessage
        ? [conversation.lastMessage]
        : []
    const project = conversation.projectId ? projectById.value[conversation.projectId] : null
    if (conversation.kind === 'project' && !project) continue
    for (const message of pool) {
      if (!(message.text || '').toLowerCase().includes(q)) continue
      hits.push({
        id: `${conversation.uuid}-${message.id}`,
        isProject: conversation.kind === 'project',
        project,
        conversationKey: conversation.uuid,
        peer: conversation.peer,
        title: conversation.kind === 'project' ? project.project_name : conversation.peer?.display_name,
        author: message.author?.display_name || 'Unknown',
        text: message.text,
        at: message.createdAt || 0,
      })
    }
  }

  return hits.sort((a, b) => b.at - a.at).slice(0, 20)
})

const hasResults = computed(
  () =>
    people.value.length ||
    agentResults.value.length ||
    projectResults.value.length ||
    messageResults.value.length
)

// The conversation list (DMs + project chats with their last message and
// unread counts) comes from the server in one call.
chatStore.fetchConversations().catch(() => {})

const boundaries = ref({})
watch(
  () => projects.value.length,
  async () => {
    if (!projects.value.length) return
    const ids = projects.value.map((p) => p.project_id)
    try {
      const features = await boundaryStore.fetchBoundariesByIds(ids)
      boundaries.value = Object.fromEntries(features.map((f) => [f.properties.project_id, f]))
    } catch (e) {
      boundaries.value = {}
    }
  },
  { immediate: true }
)

const previewFailed = ref({})
const projectPreview = (project) => {
  if (previewFailed.value[project.project_id]) return null
  const boundary = boundaries.value[project.project_id]
  return boundary ? buildStaticMapUrl(boundary, { width: 96, height: 96 }) : null
}

// One flat list: direct conversations and project chats, newest activity first.
// Project chats only show once they have messages — the rest stay reachable
// through search rather than padding the list.
const items = computed(() =>
  chatStore.conversations
    .map((c) => {
      const last = c.lastMessage
      if (c.kind === 'project') {
        const project = projectById.value[c.projectId]
        if (!project || !last) return null
        return {
          key: c.uuid,
          isProject: true,
          project,
          title: project.project_name,
          preview: `${last.author?.display_name || 'Someone'}: ${last.text}`,
          at: c.lastAt || 0,
          unread: c.unreadCount,
        }
      }
      if (!c.peer) return null
      return {
        key: c.uuid,
        isProject: false,
        peer: c.peer,
        title: c.peer.display_name,
        preview: last?.text || 'No messages yet',
        at: c.lastAt || 0,
        unread: c.unreadCount,
      }
    })
    .filter(Boolean)
    .sort((a, b) => b.at - a.at)
)

const clearSearch = () => {
  if (newChatOpen.value) messagingStore.setNewChatOpen(false)
  else messagingStore.setSearchQuery('')
  searchPeople.value = []
}

const openMember = (member) => {
  messagingStore.openConversation({
    kind: 'member',
    peer: {
      member_id: member.member_id,
      display_name: member.display_name,
      avatar_url: member.avatar_url || null,
      role: accessStore.role(member.role_id)?.name ?? null,
    },
  })
  clearSearch()
}

const openAgent = (agent) => {
  messagingStore.openConversation({
    kind: 'agent',
    peer: {
      member_id: `agent:${agent.id}`,
      display_name: agent.name,
      avatar_url: null,
      emoji: agent.emoji || null,
      role: 'Agent',
      agent_id: agent.id,
      is_agent: true,
    },
  })
  clearSearch()
}

const openProject = (project) => {
  clearSearch()
  router.push({
    name: 'ProjectChat',
    params: { org_id: route.params.org_id || orgId.value, project_id: project.project_id },
  })
}

const openDmConversation = (uuid) => {
  messagingStore.setActive(uuid)
  chatStore.ensureMessages(uuid).then(() => {
    chatStore.markRead(uuid)
  })
}

const openItem = (item) => {
  if (item.isProject) openProject(item.project)
  else openDmConversation(item.key)
}

const openMessageHit = (hit) => {
  if (hit.isProject) {
    openProject(hit.project)
    return
  }
  clearSearch()
  openDmConversation(hit.conversationKey)
}
</script>

<template>
  <div class="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
    <!-- Search results, and the new-chat picker that reuses them -->
    <template v-if="showSections">
      <div
        v-if="!hasResults"
        class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
      >
        <template v-if="isPicking">Nobody left to message</template>
        <template v-else>Nothing matches “{{ query.trim() }}”</template>
      </div>

      <template v-else>
        <div v-if="agentResults.length" class="mb-3">
          <div class="mb-1 px-1 text-xs font-medium text-muted-foreground">Agents</div>
          <button
            v-for="agent in agentResults"
            :key="`agent-${agent.id}`"
            class="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left transition-colors hover:bg-muted"
            @click="openAgent(agent)"
          >
            <ChatAvatar :author="{ display_name: agent.name, emoji: agent.emoji }" size="base" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium">{{ agent.name }}</div>
              <div class="truncate text-xs text-muted-foreground">{{ agent.description }}</div>
            </div>
          </button>
        </div>

        <div v-if="people.length" class="mb-3">
          <div class="mb-1 px-1 text-xs font-medium text-muted-foreground">People</div>
          <button
            v-for="member in people"
            :key="`member-${member.member_id}`"
            class="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left transition-colors hover:bg-muted"
            @click="openMember(member)"
          >
            <ChatAvatar :author="member" size="base" />
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium">{{ member.display_name }}</div>
              <div class="truncate text-xs text-muted-foreground">{{ member.email }}</div>
            </div>
          </button>
        </div>

        <div v-if="projectResults.length">
          <div class="mb-1 px-1 text-xs font-medium text-muted-foreground">Projects</div>
          <button
            v-for="project in projectResults"
            :key="`project-${project.project_id}`"
            class="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left transition-colors hover:bg-muted"
            @click="openProject(project)"
          >
            <div class="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted">
              <img
                v-if="projectPreview(project)"
                :src="projectPreview(project)"
                :alt="`Map preview of ${project.project_name}`"
                class="h-full w-full object-cover"
                loading="lazy"
                @error="previewFailed[project.project_id] = true"
              />
              <span v-else-if="project.emoji" class="text-sm leading-none">{{ project.emoji }}</span>
              <Folder v-else class="size-3.5 text-muted-foreground" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium">{{ project.project_name }}</div>
              <div class="truncate text-xs text-muted-foreground">Open project chat</div>
            </div>
            <Badge variant="secondary" class="shrink-0 px-1.5 py-0 text-[10px] font-medium">Project</Badge>
          </button>
        </div>

        <div v-if="messageResults.length" class="mt-3">
          <div class="mb-1 px-1 text-xs font-medium text-muted-foreground">Messages</div>
          <button
            v-for="hit in messageResults"
            :key="hit.id"
            class="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left transition-colors hover:bg-muted"
            @click="openMessageHit(hit)"
          >
            <div
              v-if="hit.isProject"
              class="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted"
            >
              <img
                v-if="projectPreview(hit.project)"
                :src="projectPreview(hit.project)"
                :alt="`Map preview of ${hit.title}`"
                class="h-full w-full object-cover"
                loading="lazy"
                @error="previewFailed[hit.project.project_id] = true"
              />
              <span v-else-if="hit.project.emoji" class="text-sm leading-none">{{ hit.project.emoji }}</span>
              <Folder v-else class="size-3.5 text-muted-foreground" />
            </div>
            <ChatAvatar v-else :author="hit.peer" size="base" />

            <div class="min-w-0 flex-1">
              <div class="flex items-baseline gap-1.5">
                <span class="truncate text-sm font-medium">{{ hit.title }}</span>
                <Badge
                  v-if="hit.isProject"
                  variant="secondary"
                  class="shrink-0 px-1.5 py-0 text-[10px] font-medium"
                >
                  Project
                </Badge>
                <span v-if="hit.at" class="ml-auto shrink-0 text-xs text-muted-foreground">
                  {{ formatTimeFromNow(new Date(hit.at).toISOString()) }}
                </span>
              </div>
              <div class="truncate text-xs text-muted-foreground">{{ hit.author }}: {{ hit.text }}</div>
            </div>
          </button>
        </div>
      </template>
    </template>

    <!-- Conversations + project chats, one flat list -->
    <template v-else>
      <div
        v-if="!items.length"
        class="rounded-lg border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
      >
        <MessagesSquare class="mx-auto mb-2 size-6" />
        No conversations yet — tap + to start one
      </div>

      <button
        v-for="item in items"
        :key="item.key"
        class="flex w-full items-center gap-2.5 rounded-lg p-2.5 text-left transition-colors hover:bg-muted"
        @click="openItem(item)"
      >
        <div
          v-if="item.isProject"
          class="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted"
        >
          <img
            v-if="projectPreview(item.project)"
            :src="projectPreview(item.project)"
            :alt="`Map preview of ${item.title}`"
            class="h-full w-full object-cover"
            loading="lazy"
            @error="previewFailed[item.project.project_id] = true"
          />
          <span v-else-if="item.project.emoji" class="text-sm leading-none">{{ item.project.emoji }}</span>
          <Folder v-else class="size-3.5 text-muted-foreground" />
        </div>
        <ChatAvatar v-else :author="item.peer" size="base" />

        <div class="min-w-0 flex-1">
          <div class="flex items-baseline gap-1.5">
            <span class="truncate text-sm font-medium">{{ item.title }}</span>
            <Badge
              v-if="item.isProject"
              variant="secondary"
              class="shrink-0 px-1.5 py-0 text-[10px] font-medium"
            >
              Project
            </Badge>
            <span v-if="item.at" class="ml-auto shrink-0 text-xs text-muted-foreground">
              {{ formatTimeFromNow(new Date(item.at).toISOString()) }}
            </span>
          </div>
          <div class="flex items-center gap-1.5">
            <span class="min-w-0 flex-1 truncate text-xs text-muted-foreground">{{ item.preview }}</span>
            <span
              v-if="item.unread"
              class="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white"
            >
              {{ item.unread > 99 ? '99+' : item.unread }}
            </span>
          </div>
        </div>
      </button>
    </template>
  </div>
</template>
